import { Injectable } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { filterNil } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import {
	ComponentController,
	MessagingService,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import {
	finalize,
	first,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { CoachSettingsGeneralFormController } from 'presentation/page/settings/coach-settings/coach-settings-general/coach-settings-general-form.controller';
import { UserReferenceOutput } from 'user/model/user-reference.output';
import { UserApplicationPort } from 'user/port/user-application.port';

import { AppService } from '../../../../app.service';
import {
	CoachSettingsGeneralState,
	CoachSettingsGeneralStore,
} from './coach-settings-general.store';

@Injectable()
@UntilDestroy()
export class CoachSettingsGeneralController extends ComponentController<CoachSettingsGeneralState> {
	public isSaving$: Observable<boolean>;
	public formValid$: Observable<boolean>;
	public calendarLocale$: Observable<any>;

	constructor(
		private _fb: FormBuilder,
		private _appService: AppService,
		store: CoachSettingsGeneralStore,
		formsManager: AkitaNgFormsManager,
		private _routerQuery: RouterQuery,
		private _messagingService: MessagingService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _formController: CoachSettingsGeneralFormController
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public updateUser(): Promise<void> {
		this.store.update({ isSaving: true });

		return this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				switchMap((userId) =>
					this._userApplicationPort.updateCoach({
						certificates: (this._formController.form.value.certificates || []).map(
							(certificate) =>
								({
									id: certificate.id,
									endDate: certificate.range[1] || new Date(),
									institution: certificate.institution,
									startDate: certificate.range[0],
									name: certificate.name,
								} as UserReferenceOutput)
						),
						diplomas: (this._formController.form.value.diplomas || []).map(
							(diploma) =>
								({
									id: diploma.id,
									endDate: diploma.range[1] || new Date(),
									institution: diploma.institution,
									startDate: diploma.range[0],
									name: diploma.name,
								} as UserReferenceOutput)
						),
						sectors: (this._formController.form.value.sectors || []).join(''),
						sessionPrice: this._formController.form.value.sessionPrice,
						phoneNumber: this._formController.form.value.phoneNumber,
						firstName: this._formController.form.value.firstName,
						birthDate: this._formController.form.value.birthDate,
						biography: this._formController.form.value.biography,
						planPrice: this._formController.form.value.planPrice,
						timeZone: this._formController.form.value.timeZone,
						lastName: this._formController.form.value.lastName,
						currentAddress: this._formController.form.value.currentAddress,
						birthAddress: this._formController.form.value.birthAddress,
						linkedInUrl: this._formController.form.value.linkedInUrl,
						id: userId,
					})
				),
				finalize(() => this.store.update({ isSaving: false })),
				tap(() =>
					this._messagingService.publishMessage({
						summaryKey: 'presentation.app.page.settings.coach.general.saved.summary',
						detailKey: 'presentation.app.page.settings.coach.general.saved.detail',
						severity: 'success',
					})
				),
				first()
			)
			.toPromise();
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				filterNil,
				switchMap((coach) =>
					coach.organization$.pipe(
						tap((org) => {
							const certificatesArray = this._formController.form.get(
								'certificates'
							) as FormArray;
							const diplomasArray = this._formController.form.get(
								'diplomas'
							) as FormArray;

							diplomasArray.clear();
							certificatesArray.clear();
							coach.certificates.forEach((certificate) =>
								certificatesArray.push(
									this._formController.buildReferenceGroup(certificate)
								)
							);
							coach.diplomas.forEach((diploma) =>
								diplomasArray.push(this._formController.buildReferenceGroup(diploma))
							);

							this._formController.form.patchValue({
								sessionPrice: {
									amount: org?.sessionPrice?.amount,
									currency: org?.sessionPrice?.currency || 'EUR',
								},
								planPrice: {
									amount: org?.planPrice?.amount,
									currency: org?.sessionPrice?.currency || 'EUR',
								},
								birthDate: coach?.metadata.birthDate || null,
								biography: org?.shortDescription || '',
								sectors: org?.sectors?.split(''),
								phoneNumber: coach?.phoneNumber,
								firstName: coach?.firstName,
								lastName: coach?.lastName,
								timeZone: coach?.timeZone,
								birthAddress: coach?.metadata.originAddress || '',
								currentAddress: coach?.metadata.livingAddress || '',
								linkedInUrl: coach?.metadata.linkedInUrl || '',
							});
						})
					)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.formValid$ = combineLatest([
			this._formController.selectValid('sessionPrice'),
			this._formController.selectValid('planPrice'),
			this._formController.selectValid('certificates'),
			this._formController.selectValid('diplomas'),
			this._formController.selectValid('biography'),
			this._formController.selectValid('firstName'),
			this._formController.selectValid('lastName'),
			this._formController.selectValid('phoneNumber'),
			this._formController.selectValid('birthDate'),
			this._formController.selectValid('sectors'),
		]).pipe(
			map((data) => data.every((d) => d)),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSaving$ = this.select('isSaving');
	}
}
